import apiClient from './meta-client';
import type { ContentBlock } from '@/types/flow';
import type { BroadcastFormData } from '@/pages/Meta/Broadcast/types/broadcast.types';

export interface UltimaExecucao {
    data: string;
    status: 'executando' | 'ok' | 'parcial' | 'falha';
    grupos_criados: number;
    broadcasts_criados: number;
    paginas_puladas: number;
    erro: string | null;
    /** Observações que NÃO são falha — ficavam no campo `erro` e a tela pintava de vermelho. */
    avisos: string | null;
    criado_em: string;
}

export interface Rotina {
    id: number;
    user_id: string;
    /** Só vem para admin. */
    owner_name?: string | null;
    nome: string;
    dia_semana: number;
    ativa: number;
    criado_em: string;
    atualizado_em: string;
    ultima_execucao?: UltimaExecucao | null;
    /** Data (ISO) em que as horas de Brasília dos grupos foram calculadas: a próxima execução. */
    data_brt?: string;
}

export interface RotinaGrupo {
    id: number;
    rotina_id: number;
    ordem: number;
    titulo_modelo: string;
    titulo_com_data_hora: number;
    /** O que o molde guarda: a hora no relógio de cada página. É ela que o editor edita. */
    hora_local: string;
    /**
     * A mesma hora no relógio de Brasília, como as telas de broadcast e de
     * calendário mostram. Vem calculada na data da próxima execução, para o
     * horário de verão europeu entrar na conta. `hora_brt_fim` difere de
     * `hora_brt` quando o grupo tem páginas em mais de um fuso. Null enquanto
     * o grupo não tem página.
     */
    hora_brt: string | null;
    hora_brt_fim: string | null;
    message: string | null;
    /** O conteúdo vive aqui (`blocks`); a coluna volta como objeto ou como texto JSON. */
    template_params: { blocks?: ContentBlock[] } | string | null;
    /**
     * Mapa `{idioma: texto}` que o import gravou, ou null. O GET devolve a coluna
     * (o repositório faz `SELECT *`); é a única forma de a tela saber se o grupo
     * tem tradução guardada sem abrir o editor.
     */
    translated_template: Record<string, string> | string | null;
    ai_translate: number;
    target_language: string | null;
    source_language: string | null;
    message_tag: string | null;
    utility_template_id: number | null;
    secondary_utility_template_id: number | null;
    /** Esteira de utility templates; resolvida a cada materialização, nunca congelada. */
    utility_esteira_id: number | null;
    use_orchestrator: number;
    ativa: number;
    paginas: string[];
}

export interface RotinaCompleta extends Rotina {
    grupos: RotinaGrupo[];
}

/**
 * O grupo não tem campo `blocks`: o conteúdo vai em `template_params.blocks`, que é o que a
 * materialização lê. `blocks` solto seria descartado em silêncio pelo Pydantic e o grupo
 * gravaria sem conteúdo.
 */
export interface RotinaGrupoPayload {
    titulo_modelo: string;
    /** Marcado, a materialização compõe "... 30/09 (7:15)". Desmarcado, o título sai literal. */
    titulo_com_data_hora: boolean;
    hora_local: string;
    template_params: { blocks: ContentBlock[] };
    message_tag: string | null;
    /**
     * Tipo do grupo. Os três são excludentes e seguem o contrato da tela de
     * broadcast: standard/tag usa `paginas`; utility usa o template; esteira
     * resolve as próprias páginas e ignora `paginas`.
     */
    utility_template_id: number | null;
    secondary_utility_template_id: number | null;
    utility_esteira_id: number | null;
    ai_translate: boolean;
    source_language: string | null;
    use_orchestrator: boolean;
    paginas: string[];
}

export interface RotinaGrupoUpdate extends Partial<RotinaGrupoPayload> {
    ativa?: boolean;
}

/** O POST devolve 201 mesmo com `erro_disparo_hoje` preenchido: o grupo foi gravado. */
export interface RotinaGrupoCriado extends RotinaGrupo {
    broadcasts_criados_hoje: number;
    agendados_no_passado: number;
    /** Páginas pausadas ou ausentes: ficaram de fora e não receberam disparo. */
    paginas_puladas: number;
    /** Broadcasts que nasceram `failed` (token, por exemplo): existem e não vão sair. */
    broadcasts_falhos: number;
    erro_disparo_hoje: string | null;
    /** Dia (AAAA-MM-DD) cujos disparos o grupo alcançou: hoje, ou amanhã se já materializado às 20h. */
    data_disparos: string | null;
}

export interface RotinaExcluida {
    rotina_id: number;
    grupos_apagados: number;
    broadcasts_desvinculados: number;
}

export interface GrupoExcluido {
    grupo_id: number;
    broadcasts_desvinculados: number;
}

/** `propagou: false` = nenhum campo propagável, contadores null. `true` com `atualizados: 0` = tentou e nada casou. */
export interface RotinaGrupoSalvo extends RotinaGrupo {
    propagou: boolean;
    atualizados: number | null;
    ignorados_ja_iniciados: number | null;
    agendados_no_passado: number | null;
    sem_traducao_no_mapa: number | null;
    com_traducao_do_mapa: number | null;
    sem_page_id: number | null;
    com_erro: number | null;
    aviso_traducao: string | null;
    erro_propagacao: string | null;
    valem_na_proxima_materializacao: string[];
    /**
     * Gravado no grupo e que NUNCA chega a um broadcast, nem hoje nem na próxima
     * materialização (hoje só `app_id`). Diferente de
     * `valem_na_proxima_materializacao`: ali é "ainda vai valer", aqui é "não vai
     * valer nunca". O backend manda desde sempre; a tela precisa dizer.
     */
    campos_ignorados: string[];
    motivo_ignorados: Record<string, string>;
}

export interface RotinaImportarInput {
    data: string;
    dia_semana: number;
    nome: string;
}

export interface RotinaImportResultado {
    rotina_id: number;
    grupos_importados: number;
    /**
     * `outro_dia` separa o descarte esperado — grupo do dia vizinho que a
     * margem de fuso da busca trouxe — do que merece atenção de verdade.
     */
    grupos_ignorados: {
        group_broadcast_id: number;
        motivo: string;
        outro_dia: boolean;
        titulo: string;
        dias_locais: string[];
    }[];
    grupos: {
        titulo_modelo: string;
        /** Grupo de template utility: não tem tradução própria, e isso não é problema. */
        utility: boolean;
        hora_local: string;
        paginas: number;
        idiomas_traduzidos: number;
        linhas_sem_traducao: number;
        traducao_sem_idioma: number;
        paginas_fora_do_dia: number;
        /** Broadcasts cancelados do grupo: não entram no molde, senão a Rotina os ressuscitaria todo dia. */
        paginas_canceladas: number;
        paginas_sem_page_id: number;
    }[];
    linhas_sem_traducao: number;
    avisos: string[];
}

export interface RotinaDivergencia {
    rotina_id: number;
    na_rotina_pausadas: { page_id: string; page_name: string | null; grupo_ids: number[] }[];
    na_rotina_nao_encontradas: { page_id: string; grupo_ids: number[] }[];
    ativas_fora_da_rotina: { page_id: string; page_name: string | null }[];
}

/** `detail` é string nos erros de regra (400) e lista de objetos nos 422 de validação. */
export function mensagemDeErro(e: unknown, padrao: string): string {
    const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        const msgs = detail.map((d: { loc?: unknown[]; msg?: string }) =>
            `${(d.loc ?? []).filter((x) => x !== 'body').join('.')}: ${d.msg ?? ''}`.trim());
        if (msgs.length) return msgs.join('; ');
    }
    return padrao;
}

export function statusDeErro(e: unknown): number | undefined {
    return (e as { response?: { status?: number } })?.response?.status;
}

export function blocosDoGrupo(tp: RotinaGrupo['template_params']): ContentBlock[] {
    let obj: unknown = tp;
    if (typeof tp === 'string') {
        try { obj = JSON.parse(tp); } catch { return []; }
    }
    const blocks = (obj as { blocks?: unknown } | null)?.blocks;
    return Array.isArray(blocks) ? (blocks as ContentBlock[]) : [];
}

/**
 * Espelha `handleCreateBroadcast` (Broadcast.tsx): mesma origem de cada campo, trocando
 * `is_scheduled`/`scheduled_at` por `hora_local`. Utility e Esteira ficam de fora: o modo
 * Rotina só adapta o fluxo standard.
 */
export function grupoDoFormulario(data: BroadcastFormData): RotinaGrupoPayload {
    return {
        titulo_modelo: (data.title ?? '').trim(),
        titulo_com_data_hora: data.tituloComDataHora !== false,
        hora_local: data.horaLocal ?? '',
        template_params: { blocks: data.blocks },
        message_tag: data.broadcastType === 'message_tag' ? (data.messageTag ?? null) : null,
        utility_template_id: data.broadcastType === 'utility' ? (data.utilityTemplateId ?? null) : null,
        secondary_utility_template_id:
            data.broadcastType === 'utility' ? (data.secondaryUtilityTemplateId ?? null) : null,
        utility_esteira_id: data.broadcastType === 'esteira' ? (data.selectedEsteiraId ?? null) : null,
        // Utility e Esteira não traduzem: o texto já foi aprovado por página no
        // template, e marcar tradução prende o broadcast em 'translating'.
        ai_translate: data.broadcastType === 'standard' || data.broadcastType === 'message_tag'
            ? !!data.aiTranslate : false,
        source_language: data.sourceLanguage || null,
        // Sempre true: o orquestrador é o único caminho de envio desde 06/10/2026.
        // A coluna permanece no grupo porque o backend a declara NOT NULL.
        use_orchestrator: true,
        paginas: data.selectedPages.map((p) => String(p.page_id)).filter(Boolean),
    };
}

export interface RotinaConflitos {
    rotina_id: number;
    nome: string;
    dia_semana: number;
    total_paginas: number;
    rotinas: {
        rotina_id: number;
        nome: string;
        paginas: { page_id: string; page_name: string | null }[];
    }[];
}

const BASE = '/meta/rotinas';

export const listRotinasAgendamento = async (userId?: number): Promise<Rotina[]> => {
    const response = await apiClient.get(BASE, { params: { user_id: userId } });
    return response.data;
};

export const getRotinaAgendamento = async (id: number): Promise<RotinaCompleta> => {
    const response = await apiClient.get(`${BASE}/${id}`);
    return response.data;
};

export const getRotinaConflitos = async (id: number): Promise<RotinaConflitos> => {
    const response = await apiClient.get(`${BASE}/${id}/conflitos`);
    return response.data;
};

/** O que entra e o que sai de cada grupo ao sincronizá-lo com as suas categorias. */
export interface RotinaAtualizacaoCategorias {
    rotina_id: number;
    aplicado: boolean;
    /** Páginas que ficaram de fora por já estarem em outra Rotina ligada do mesmo dia. */
    em_outra_rotina: number;
    grupos: {
        grupo_id: number;
        hora_local: string;
        entram: { page_id: string; page_name: string | null; categoria: string | null }[];
        saem: {
            page_id: string;
            page_name: string | null;
            motivo: 'pausada' | 'app desligado' | 'fora da conta' | 'categoria desmarcada';
        }[];
    }[];
    /** Todas as categorias com página ativa na conta. */
    categorias: { id: number; nome: string; paginas: number; grupos_que_usam: number }[];
}

/** Sem `categorias`, cada grupo segue as que já usa. Com, todos os grupos ficam com elas. */
export const getPreviaCategorias = async (id: number, categorias?: number[]): Promise<RotinaAtualizacaoCategorias> => {
    // FastAPI lê lista repetindo a chave (?categorias=1&categorias=2), não no formato `categorias[]` do axios.
    const qs = categorias ? `?${categorias.map((c) => `categorias=${c}`).join('&')}` : '';
    const response = await apiClient.get(`${BASE}/${id}/categorias${qs}`);
    return response.data;
};

export const atualizarPorCategorias = async (id: number, categorias: number[]): Promise<RotinaAtualizacaoCategorias> => {
    const response = await apiClient.post(`${BASE}/${id}/categorias`, { categorias });
    return response.data;
};

export const getRotinaDivergencia = async (id: number): Promise<RotinaDivergencia> => {
    const response = await apiClient.get(`${BASE}/${id}/divergencia`);
    return response.data;
};

export const updateRotinaGrupo = async (
    rotinaId: number,
    grupoId: number,
    data: RotinaGrupoUpdate,
): Promise<RotinaGrupoSalvo> => {
    const response = await apiClient.put(`${BASE}/${rotinaId}/grupos/${grupoId}`, data);
    return response.data;
};

export const importarRotinaDeData = async (data: RotinaImportarInput): Promise<RotinaImportResultado> => {
    const response = await apiClient.post(`${BASE}/importar`, data);
    return response.data;
};

export const criarRotinaVazia = async (nome: string, diaSemana: number): Promise<RotinaCompleta> => {
    const response = await apiClient.post(BASE, { nome, dia_semana: diaSemana });
    return response.data;
};

export const criarRotinaGrupo = async (rotinaId: number, data: RotinaGrupoPayload): Promise<RotinaGrupoCriado> => {
    const response = await apiClient.post(`${BASE}/${rotinaId}/grupos`, data);
    return response.data;
};

export const excluirRotinaGrupo = async (rotinaId: number, grupoId: number): Promise<GrupoExcluido> => {
    const response = await apiClient.delete(`${BASE}/${rotinaId}/grupos/${grupoId}`);
    return response.data;
};

export const atualizarRotina = async (
    rotinaId: number,
    data: { nome?: string; ativa?: boolean },
): Promise<RotinaCompleta> => {
    const response = await apiClient.put(`${BASE}/${rotinaId}`, data);
    return response.data;
};

export const excluirRotina = async (rotinaId: number): Promise<RotinaExcluida> => {
    const response = await apiClient.delete(`${BASE}/${rotinaId}`);
    return response.data;
};
